import { useEffect, useState } from "react";
import Icon from "../../components/Icon";
import LoadingBlock from "../../components/LoadingBlock";
import EmptyState from "../../components/EmptyState";
import Drawer from "../../components/Drawer";
import Pagination from "../../components/Pagination";
import SearchableSelect from "../../components/SearchableSelect";
import { useApi } from "../../hooks/useApi";
import {
  listLeaves,
  approveLeave,
  rejectLeave,
  adminCancelLeave,
  listLeaveTypes,
  createLeaveType,
  updateLeaveType,
  deleteLeaveType,
  listLeaveBalances,
  updateLeaveBalance,
} from "../../api/leaveService";
import { listAttendanceStaff } from "../../api/attendanceService";
import { paginatedRows, paginatedMeta } from "../../utils/pagination";
import { nowIst, formatDate, formatDateTime } from "../../utils/formatters";
import { showApiError, showToast } from "../../utils/toast";

const STATUS_BADGE = {
  pending: "badge-warning",
  approved: "badge-success",
  rejected: "badge-danger",
  cancelled: "badge-neutral",
};

export default function AdminLeavesPage() {
  const [tab, setTab] = useState("approvals");

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Leaves</h1>
          <div className="page-sub">Approvals inbox, the full application log, leave types and per-person balances.</div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="tabs" style={{ marginBottom: 14 }}>
            <button className={`tab ${tab === "approvals" ? "active" : ""}`} onClick={() => setTab("approvals")}>Approvals</button>
            <button className={`tab ${tab === "all" ? "active" : ""}`} onClick={() => setTab("all")}>All applications</button>
            <button className={`tab ${tab === "types" ? "active" : ""}`} onClick={() => setTab("types")}>Types</button>
            <button className={`tab ${tab === "balances" ? "active" : ""}`} onClick={() => setTab("balances")}>Balances</button>
          </div>

          {tab === "approvals" && <ApplicationsTab pendingOnly />}
          {tab === "all" && <ApplicationsTab />}
          {tab === "types" && <TypesTab />}
          {tab === "balances" && <BalancesTab />}
        </div>
      </div>
    </>
  );
}

/* ── Applications (approvals inbox + full log) ────────────────────────── */

function ApplicationsTab({ pendingOnly = false }) {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [status, setStatus] = useState(pendingOnly ? "pending" : "");
  const [userId, setUserId] = useState("");
  const [review, setReview] = useState(null); // {leave, action}

  const staff = useApi(() => listAttendanceStaff());
  const leaves = useApi(
    () => listLeaves({ page, per_page: perPage, status: status || undefined, user_id: userId || undefined }),
    [page, perPage, status, userId],
  );

  const rows = paginatedRows(leaves.data);
  const meta = paginatedMeta(leaves.data);
  const staffOptions = (staff.data?.data || []).map((u) => ({
    id: u.id,
    label: u.name,
    hint: u.role === "admin" ? "Admin" : u.department || "Employee",
  }));

  return (
    <>
      <div className="row gap-md" style={{ marginBottom: 10, flexWrap: "wrap" }}>
        {!pendingOnly && (
          <select className="select" style={{ width: 150 }} value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="cancelled">Cancelled</option>
          </select>
        )}
        <SearchableSelect
          value={userId}
          onChange={(v) => { setUserId(v || ""); setPage(1); }}
          options={[{ id: "", label: "Everyone" }, ...staffOptions]}
          placeholder="Filter by person"
          width={220}
        />
      </div>

      {leaves.loading ? (
        <LoadingBlock rows={4} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon="inbox"
          title={pendingOnly ? "Inbox zero" : "No applications"}
          hint={pendingOnly ? "No leave applications waiting on you." : "Nothing matches these filters."}
        />
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Person</th>
              <th>Type</th>
              <th>Dates</th>
              <th className="t-num">Days</th>
              <th>Reason</th>
              <th>Status</th>
              <th style={{ width: 160 }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((l) => (
              <tr key={l.id}>
                <td className="t-primary fw-500">{l.user?.name}</td>
                <td>{l.leave_type?.name}</td>
                <td>
                  {formatDate(l.start_date)}
                  {l.start_date !== l.end_date ? ` → ${formatDate(l.end_date)}` : ""}
                  {l.is_half_day ? " (½)" : ""}
                </td>
                <td className="t-num">{Number(l.days)}</td>
                <td className="t-muted" style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={l.reason}>
                  {l.reason}
                </td>
                <td>
                  <span className={`badge ${STATUS_BADGE[l.status] || "badge-neutral"}`}>{l.status}</span>
                  {l.reviewer?.name && (
                    <div className="t-muted text-xs" title={formatDateTime(l.reviewed_at)}>
                      by {l.reviewer.name}
                    </div>
                  )}
                </td>
                <td>
                  <div className="row gap-sm" style={{ justifyContent: "flex-end" }}>
                    {l.status === "pending" && (
                      <>
                        <button className="btn btn-primary" onClick={() => setReview({ leave: l, action: "approve" })}>
                          Approve
                        </button>
                        <button className="btn btn-secondary" onClick={() => setReview({ leave: l, action: "reject" })}>
                          Reject
                        </button>
                      </>
                    )}
                    {l.status === "approved" && (
                      <button className="btn btn-secondary" onClick={() => setReview({ leave: l, action: "cancel" })}>
                        Cancel leave
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Pagination meta={meta} page={page} onPageChange={setPage} perPage={perPage} onPerPageChange={setPerPage} disabled={leaves.loading} />

      <ReviewDrawer
        review={review}
        onClose={() => setReview(null)}
        onDone={() => {
          setReview(null);
          leaves.reload();
        }}
      />
    </>
  );
}

function ReviewDrawer({ review, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => setNote(""), [review]);
  if (!review) return null;

  const { leave, action } = review;
  const titles = { approve: "Approve leave", reject: "Reject leave", cancel: "Cancel approved leave" };

  const submit = async () => {
    setSaving(true);
    try {
      if (action === "approve") await approveLeave(leave.id, note || undefined);
      if (action === "reject") await rejectLeave(leave.id, note || undefined);
      if (action === "cancel") await adminCancelLeave(leave.id, note || undefined);
      showToast("success", `Leave ${action === "approve" ? "approved" : action === "reject" ? "rejected" : "cancelled"}`);
      onDone?.();
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={titles[action]}
      subtitle={`${leave.user?.name} · ${leave.leave_type?.name} · ${formatDate(leave.start_date)}${leave.start_date !== leave.end_date ? ` → ${formatDate(leave.end_date)}` : ""} (${Number(leave.days)} day${Number(leave.days) === 1 ? "" : "s"})`}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Back</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? "…" : titles[action]}
          </button>
        </>
      }
    >
      <div className="col gap-md">
        <div className="field">
          <label className="field-label">Reason given</label>
          <div className="text-sm">{leave.reason}</div>
        </div>
        <div className="field">
          <label className="field-label">Note {action === "approve" ? "(optional)" : "(recommended)"}</label>
          <textarea className="textarea" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Visible to the applicant" />
        </div>
        {action === "cancel" && (
          <div className="t-muted text-xs">
            Cancelling reverts the affected attendance days and returns the balance.
          </div>
        )}
      </div>
    </Drawer>
  );
}

/* ── Types ───────────────────────────────────────────────────────────── */

function TypesTab() {
  const types = useApi(() => listLeaveTypes());
  const [drawer, setDrawer] = useState(null);

  const rows = types.data?.data || [];

  const onDelete = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    try {
      await deleteLeaveType(t.id);
      showToast("success", "Leave type deleted");
      types.reload();
    } catch (err) {
      showApiError(err);
    }
  };

  return (
    <>
      <div className="row" style={{ justifyContent: "flex-end", marginBottom: 10 }}>
        <button className="btn btn-primary" onClick={() => setDrawer({})}>
          <Icon name="plus" size={14} /> New type
        </button>
      </div>
      {types.loading ? (
        <LoadingBlock rows={3} />
      ) : rows.length === 0 ? (
        <EmptyState icon="layers" title="No leave types" hint="Create Casual / Sick / Earned leave with yearly quotas." />
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th className="t-num">Annual quota</th>
              <th>Paid</th>
              <th>Status</th>
              <th className="t-num">Applications</th>
              <th style={{ width: 90 }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.id}>
                <td className="t-primary fw-500">{t.name}</td>
                <td className="t-num">{Number(t.annual_quota)}</td>
                <td>{t.is_paid ? "Yes" : "No"}</td>
                <td>{t.is_active ? <span className="badge badge-success">Active</span> : <span className="badge badge-neutral">Off</span>}</td>
                <td className="t-num t-muted">{t.applications_count ?? 0}</td>
                <td>
                  <div className="row gap-sm" style={{ justifyContent: "flex-end" }}>
                    <button className="icon-btn" title="Edit" onClick={() => setDrawer({ type: t })}>
                      <Icon name="edit" size={13} />
                    </button>
                    <button className="icon-btn" title="Delete" onClick={() => onDelete(t)} style={{ color: "var(--danger)" }}>
                      <Icon name="trash" size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <TypeDrawer
        open={!!drawer}
        type={drawer?.type}
        onClose={() => setDrawer(null)}
        onSaved={() => {
          setDrawer(null);
          types.reload();
        }}
      />
    </>
  );
}

function TypeDrawer({ open, type, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [quota, setQuota] = useState(12);
  const [isPaid, setIsPaid] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(type?.name || "");
    setQuota(type ? Number(type.annual_quota) : 12);
    setIsPaid(type ? type.is_paid !== false : true);
    setIsActive(type ? type.is_active !== false : true);
    setErrors({});
  }, [open, type]);

  const submit = async () => {
    setSaving(true);
    setErrors({});
    try {
      const payload = { name: name.trim(), annual_quota: quota, is_paid: isPaid, is_active: isActive };
      if (type) await updateLeaveType(type.id, payload);
      else await createLeaveType(payload);
      showToast("success", type ? "Leave type updated" : "Leave type created");
      onSaved?.();
    } catch (err) {
      if (err?.data?.errors) setErrors(err.data.errors);
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={type ? `Edit ${type.name}` : "New leave type"}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div className="col gap-md">
        <div className="field">
          <label className="field-label">Name</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Casual Leave" />
          {errors.name && <div className="field-error">{String(errors.name)}</div>}
        </div>
        <div className="field" style={{ width: 180 }}>
          <label className="field-label">Annual quota (days)</label>
          <input type="number" step="0.5" className="input" value={quota} onChange={(e) => setQuota(Number(e.target.value))} />
          {errors.annual_quota && <div className="field-error">{String(errors.annual_quota)}</div>}
          <div className="field-hint">Seeds each person's yearly balance; adjustable per person later.</div>
        </div>
        <div className="row gap-sm">
          <input id="type-paid" type="checkbox" checked={isPaid} onChange={(e) => setIsPaid(e.target.checked)} />
          <label htmlFor="type-paid" className="text-sm" style={{ cursor: "pointer", marginBottom: 0 }}>Paid leave</label>
        </div>
        <div className="row gap-sm">
          <input id="type-active" type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          <label htmlFor="type-active" className="text-sm" style={{ cursor: "pointer", marginBottom: 0 }}>Active</label>
        </div>
      </div>
    </Drawer>
  );
}

/* ── Balances ────────────────────────────────────────────────────────── */

function BalancesTab() {
  const year = nowIst().year();
  const [userId, setUserId] = useState("");
  const [editing, setEditing] = useState(null); // balance row being edited
  const [value, setValue] = useState(0);

  const staff = useApi(() => listAttendanceStaff());
  const balances = useApi(
    () => listLeaveBalances({ year, user_id: userId || undefined }),
    [year, userId],
  );

  const rows = balances.data?.data?.rows || [];
  const staffOptions = (staff.data?.data || []).map((u) => ({
    id: u.id,
    label: u.name,
    hint: u.role === "admin" ? "Admin" : u.department || "Employee",
  }));

  const saveAllocation = async () => {
    try {
      await updateLeaveBalance(editing.balance_id, value);
      showToast("success", "Allocation updated");
      setEditing(null);
      balances.reload();
    } catch (err) {
      showApiError(err);
    }
  };

  return (
    <>
      <div className="row gap-md" style={{ marginBottom: 10 }}>
        <SearchableSelect
          value={userId}
          onChange={(v) => setUserId(v || "")}
          options={[{ id: "", label: "Everyone" }, ...staffOptions]}
          placeholder="Filter by person"
          width={220}
        />
        <span className="t-muted text-sm" style={{ alignSelf: "center" }}>Year {year}</span>
      </div>

      {balances.loading ? (
        <LoadingBlock rows={4} />
      ) : rows.length === 0 ? (
        <EmptyState icon="users" title="Nothing to show" hint="Create leave types first — balances appear per person per type." />
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Person</th>
              <th>Type</th>
              <th className="t-num">Allocated</th>
              <th className="t-num">Used</th>
              <th className="t-num">Remaining</th>
              <th style={{ width: 90 }} />
            </tr>
          </thead>
          <tbody>
            {rows.flatMap((r) =>
              (r.balances || []).map((b) => (
                <tr key={`${r.user_id}-${b.leave_type_id}`}>
                  <td className="t-primary fw-500">
                    {r.name}
                    <span className="t-muted text-xs"> {r.role === "admin" ? "· Admin" : ""}</span>
                  </td>
                  <td>{b.leave_type}{b.is_paid ? "" : " (unpaid)"}</td>
                  <td className="t-num">
                    {editing?.balance_id === b.balance_id ? (
                      <input
                        type="number"
                        step="0.5"
                        className="input"
                        style={{ width: 90 }}
                        value={value}
                        onChange={(e) => setValue(Number(e.target.value))}
                        autoFocus
                      />
                    ) : (
                      b.allocated
                    )}
                  </td>
                  <td className="t-num">{b.used}</td>
                  <td className="t-num fw-500">{b.remaining}</td>
                  <td>
                    <div className="row gap-sm" style={{ justifyContent: "flex-end" }}>
                      {editing?.balance_id === b.balance_id ? (
                        <>
                          <button className="icon-btn" title="Save" onClick={saveAllocation}>
                            <Icon name="check" size={13} />
                          </button>
                          <button className="icon-btn" title="Cancel" onClick={() => setEditing(null)}>
                            <Icon name="x" size={13} />
                          </button>
                        </>
                      ) : (
                        <button className="icon-btn" title="Adjust allocation" onClick={() => { setEditing(b); setValue(Number(b.allocated)); }}>
                          <Icon name="edit" size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )),
            )}
          </tbody>
        </table>
      )}
    </>
  );
}
